import { memo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { isServer } from '@utils/helper';
import { useGoogleAnalytics } from '@utils/hooks/useGoogleAnalytics';
import { useLeadFormCapture } from '@containers/Home/hook/useLeadFormCapture';
import { HomeWrapper } from './styled';
import {
  selectSceneSelected,
  selectDetailProject,
  selectListCallToAction,
  selectThemeConfigGlobal,
  selectIsMatterportProject,
  selectConfigProtectPassword,
  selectCallToActionConfigGlobal,
  selectDollhouseConfigGlobal,
} from './selectors';
import { useCheckPasswordTour } from './hook/useCheckPasswordTour';
import { useTourEventState } from './global/TourStats';
import { useKrpano } from './hook/useKrpano';
import Theme from './components/Theme';
import Scripts from './components/Scripts';

const DialogPost = dynamic(() => import('@components/DialogPost'));
const ProtectPassword = dynamic(() => import('@components/ProtectPassword'));
const PopupIntro = dynamic(
  () => import('@containers/Home/components/PopupIntro'),
  { ssr: false },
);
const Collaboration = dynamic(() => import('./components/Collaboration'), {
  ssr: false,
});
const DialogFormCapture = dynamic(
  () => import('@components/DialogFormCapture'),
  { ssr: false },
);
const CallToActions = dynamic(() => import('@components/CallToActions'));
const SpiritualServices = dynamic(
  () => import('../../features/SpiritualServices'),
  { ssr: false, loading: () => null },
);

const DEFAULT_SPIRITUAL_DEMO_TOUR_ID = '6a7ec92ea542d30008b5824f';
const spiritualDemoTourIds = new Set([
  DEFAULT_SPIRITUAL_DEMO_TOUR_ID,
  ...(process.env.NEXT_PUBLIC_SPIRITUAL_DEMO_TOUR_ALLOWLIST || '')
    .split(',')
    .map(value => value.trim())
    .filter(Boolean),
]);

const configuredPath = (value: string | undefined, fallback = '') => {
  if (!value) return fallback;
  try {
    return new URL(value, 'http://panoee.local').pathname || fallback;
  } catch {
    return value.split('?')[0] || fallback;
  }
};

interface Props {
  isPublic?: boolean;
  isExport?: boolean;
  isCollaborationMode?: boolean;
  // NOTE: is case custom domain => not save scene on url
  isPreventChangeUrl?: boolean;
  // NOTE: fetch cookies from server
  passwordTourCookie?: string;
  listProjectGuardOpenedCookie?: string;
  listFormCaptureOpenedCookie?: string;
}

function Home({
  isPublic,
  isExport,
  isCollaborationMode = false,
  isPreventChangeUrl = false,
  passwordTourCookie,
  listProjectGuardOpenedCookie,
  listFormCaptureOpenedCookie,
}: Props) {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null!);

  const detailProject = useSelector(selectDetailProject);
  const sceneSelected = useSelector(selectSceneSelected);
  const themeConfig = useSelector(selectThemeConfigGlobal);
  const listCallToAction = useSelector(selectListCallToAction);
  const isMatterportProject = useSelector(selectIsMatterportProject);
  const dollhouseConfigGlobal = useSelector(selectDollhouseConfigGlobal);
  const callToActionConfigGlobal = useSelector(selectCallToActionConfigGlobal);

  const configProtectPassword = useSelector(selectConfigProtectPassword);
  const routeTourId = Array.isArray(router.query.tour)
    ? router.query.tour[0]
    : router.query.tour;
  const activeSpiritualDemoTourId = [routeTourId, detailProject?.id]
    .map(value => String(value || '').trim())
    .find(value => spiritualDemoTourIds.has(value));
  const isSpiritualDemoTour =
    !isCollaborationMode && Boolean(activeSpiritualDemoTourId);

  const [tourEventStats, setTourEventStats] = useTourEventState('state');

  useEffect(() => {
    if (!isSpiritualDemoTour) return;
    setTourEventStats(prev => {
      if (
        prev.isVisiblePopupIntro === false &&
        prev.isVisibleDollhouse === false
      ) {
        return prev;
      }
      return {
        ...prev,
        isVisiblePopupIntro: false,
        isVisibleDollhouse: false,
      };
    });
  }, [isSpiritualDemoTour, setTourEventStats]);
  const { onUpdatePageViewByUrl } = useGoogleAnalytics();

  const { onCloseDialogPostProject } = useKrpano({ krpanoRef: null });

  const { isOpenGuardPassword, onCheckGuardPassword } = useCheckPasswordTour({
    detailProject,
    configProtectPassword,
    passwordTourCookie,
    listProjectGuardOpenedCookie,
  });

  const {
    onCloseFormCapture,
    formCaptureSelected,
    isVisibleFormCapture,
    formCaptureConfigGlobal,
    formCaptureConfigSelected,
    onCheckShowFormCaptureOnScene,
    onCheckShowFormCaptureOnProject,
  } = useLeadFormCapture({
    listFormCaptureOpenedCookie,
  });

  useEffect(() => {
    const tour =
      typeof router.query.tour === 'string' ? router.query.tour : undefined;

    // Next can render the page once before the dynamic route query is ready.
    // Do not write a URL during that pass: it would produce `/undefined/...`
    // and make the browser request HTML 404 pages as JS/CSS assets.
    if (
      sceneSelected &&
      router.isReady &&
      tour &&
      !isPreventChangeUrl &&
      !isMatterportProject
    ) {
      const publicBase = configuredPath(
        process.env.NEXT_PUBLIC_BASE_URL_PUBLIC,
      );
      const previewBase = configuredPath(
        process.env.NEXT_PUBLIC_BASE_URL_PREVIEW,
        '/preview',
      );
      if (!isCollaborationMode) {
        if (isExport)
          window.history.pushState(
            null,
            '',
            `${process.env.NEXT_PUBLIC_SUBFOLDER || ''}/?scene=${
              sceneSelected.id
            }`,
          );
        else
          router.push(
            isPublic
              ? `${publicBase}/${tour}/${sceneSelected.id}`
              : `${previewBase}?tour=${tour}&scene=${sceneSelected.id}`,
            undefined,
            { shallow: true },
          );
      } else {
        const collaborationBase = configuredPath(
          process.env.NEXT_PUBLIC_BASE_URL_COLLABORATION,
          '/collaboration',
        );
        const url = `${collaborationBase}?tour=${tour}&scene=${sceneSelected.id}`;
        if (!isServer) window.history.pushState(null, '', url);
      }
      onUpdatePageViewByUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneSelected?.id]);

  useEffect(() => {
    if (dollhouseConfigGlobal?.isShowOnStart)
      setTourEventStats(prev => ({
        ...prev,
        isVisibleDollhouse: !!dollhouseConfigGlobal?.isShowOnStart,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dollhouseConfigGlobal?.isShowOnStart]);

  // CHECK FORM CAPTURE TO SHOW
  useEffect(() => {
    if (isVisibleFormCapture === null && detailProject?.id)
      onCheckShowFormCaptureOnProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isVisibleFormCapture === false && sceneSelected)
      onCheckShowFormCaptureOnScene(sceneSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisibleFormCapture, sceneSelected?.id]);

  return isOpenGuardPassword ? (
    <ProtectPassword
      scene={sceneSelected}
      password={configProtectPassword?.password}
      onSubmit={onCheckGuardPassword}
    />
  ) : detailProject?.id ? (
    <>
      <HomeWrapper ref={containerRef}>
        {isCollaborationMode && <Collaboration />}
        <div className="mainContent">
          <Theme />

          {!isSpiritualDemoTour && !!listCallToAction.length && (
            <CallToActions
              listCallToAction={listCallToAction}
              callToActionConfig={callToActionConfigGlobal}
            />
          )}

          {!isSpiritualDemoTour && <PopupIntro />}

          {!!formCaptureSelected?.id && isVisibleFormCapture && (
            <DialogFormCapture
              onCancel={onCloseFormCapture}
              visible={isVisibleFormCapture}
              formCaptureSelected={formCaptureSelected}
              formCaptureConfigGlobal={formCaptureConfigGlobal}
              formCaptureConfigSelected={formCaptureConfigSelected}
            />
          )}

          {tourEventStats?.idPostProjectSelected && (
            <DialogPost
              onCancel={onCloseDialogPostProject}
              key={tourEventStats?.idPostProjectSelected}
              idPost={tourEventStats?.idPostProjectSelected}
              visible={tourEventStats?.visibleDialogPostProject}
              isUseDefaultStyle={themeConfig?.selected === 'default'}
            />
          )}
        </div>
      </HomeWrapper>

      {isSpiritualDemoTour && <SpiritualServices />}

      <Scripts />
    </>
  ) : (
    <div style={{ width: '100%', height: '100%' }}>Loading</div>
  );
}

export default memo(Home);
